import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import { orderPoints } from 'data/points-data';
import {shuffleArray} from 'helpers/array-helper';
import { getText } from 'helpers/language-helper';
import { playerUiTexts } from 'data/ui-texts/player-ui-texts';
import Button from 'components/ui/button/button';
import OrderDndItems from './order-push-dnd-items';
import TaskIntro from '../task-intro/task-intro';
import Popup from 'components/ui/popup/popup';
import './order-push.scss';

const OrderPush = (props) => {
	const {
		playerTaskData, 
		taskData,
		languageId,
		deviceInfo,
		handleCompleteTask,
		updateLoggedTime
	} = props;

	/* Check if completed already */
	const isCompleted = (playerTaskData && playerTaskData.isCompleted === true ? true : false);

	/* Get items to be ordered */
	const getOrderedItemIds = () => {
		/* Get items from player data or data file */
		let items = null;
		
		if (playerTaskData && playerTaskData.orderedItems) {
			items = playerTaskData.orderedItems;
		} else {
			// Shuffling first, before making list of items, to make sure location is in order
			const tempArray = shuffleArray(taskData.items);
			items = tempArray.map((item, index) => {
				return {id: item.id, isLocked: false, location: index};
			});
		}

		/* Special case: if facilitator and completed, show correct order */
		if (isCompleted) {
			items = taskData.items.map((item, index) => {return {id: item.id, isLocked: false, location: index};});
		}
		return items;
	};

	/* Track ordered item ids (current and prev round) and number of rounds */
	const [orderedItems, setOrderedItems] = useState([]);
	const [previewItems, setPreviewItems] = useState(null);
	/* Used to track what current preview is based on */
	const [currentPreview, setCurrentPreview] = useState(null);
	const [roundNumber, setRoundNumber] = useState(1);

	/* Popup with number of correct positions */
	const [popupData, setPopupData] = useState(null);

	/* Update sorted items if new task */
	useEffect(() => {
		setOrderedItems(getOrderedItemIds());
		setRoundNumber(1);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [taskData.id]);

	/**
	 * Switch items
	 * @param {number} fromIndex 
	 * @param {number} toIndex 
	 */
	const handleSwitchItems = () => {
		if (!previewItems) {
			return;
		}

		/* Move order of items */
		const newItems = [];

		previewItems.forEach((preview) => {
			newItems.push({id: preview.item.id, isLocked: false, location: preview.location});
		});

		/* Update logged time */
		updateLoggedTime();

		setPreviewItems(null);
		setCurrentPreview(null);
		setOrderedItems(newItems);
	};

	/**
	 * Sets a preview list with what the list should look like after switching order
	 */
	const handleSetPreview = (itemId, fromLocation, toLocation) => {
		setCurrentPreview({fromLocation: fromLocation, toLocation: toLocation});
		
		const items = JSON.parse(JSON.stringify(orderedItems));

		let fromIndex = items.findIndex((item) => {
			return item.id === itemId;
		});
		const preview = [];
		const fromItem = items[fromIndex];

		items.forEach((item) => {
			if (item.id !== itemId && toLocation > fromLocation) {
				preview.push({item: item, location: preview.length});
			}

			if (item.location === toLocation) {
				preview.push({item: fromItem, location: preview.length});
			}

			if (item.id !== itemId && toLocation < fromLocation) {
				preview.push({item: item, location: preview.length});
			}
		});

		setPreviewItems(preview);
	};

	/**
	 * Confirm order
	 */
	const confirmOrder = () => {
		/* Count errors */
		let errors = 0;
		orderedItems.forEach((item, index) => {
			const itemData = taskData.items.find((i) => {return i.id === item.id;});
			if (itemData && itemData.correctOrderNumbers && itemData.correctOrderNumbers.indexOf(index + 1) < 0) {
				errors += 1;
			}
		});

		if (errors > 0) {
			/* Show popup */
			const popupText = getText(playerUiTexts.orderPopup.text, languageId);
			const popupTitle = getText(playerUiTexts.orderPopup.title, languageId)
				.replace('%correctAnswers%', (orderedItems.length - errors))
				.replace('%totalAnswers%', orderedItems.length);
			setPopupData({
				title: popupTitle,
				text: popupText
			});
		} else {
			/* Complete task */
			completeTask();
		}
	};

	/**
	 * Close popup
	 */
	const handleClosePopup = () => {
		/* Update round number */
		const newRoundNumber = roundNumber + 1;
		setRoundNumber(newRoundNumber);

		/* Lock correctly placed items if round >= 3 */
		if (newRoundNumber >= 3) {
			let newOrderedItemIds = JSON.parse(JSON.stringify(orderedItems));
			newOrderedItemIds.forEach((item, index) => {
				const itemData = taskData.items.find((i) => {return i.id === item.id;});
				if (itemData && itemData.correctOrderNumbers && itemData.correctOrderNumbers.indexOf(index + 1) >= 0) {
					item.isLocked = true;
				}
			});
			setOrderedItems(newOrderedItemIds);
		}

		/* Close popup */
		setPopupData(null);
	};

	/**
	 * Complete task
	 */
	const completeTask = () => {
		/* Errors */ 
		const errors = (roundNumber - 1);
		
		/* Calculate points */
		let points = orderPoints.minPoints;
		let pointIndex = orderPoints.pointLimits.findIndex((limit) => {return errors <= limit;});
		if (pointIndex >= 0) points = orderPoints.pointValues[pointIndex];

		/* Save completed task */
		handleCompleteTask(
			'order', 
			points, 
			errors, 
			[{type: 'streak', isCorrectAnswer: true}],
			{orderedItems: orderedItems}
		);
	};

	/**
	 * Unsets preview list
	 */
	const handleUnsetPreview = () => {
		setPreviewItems(null);
	};

	return (
		<div className={'OrderPush'}>
			<div id="taskIntro" className="OrderPush-intro">
				<TaskIntro
					taskId={taskData.id}
					text={taskData.text ? getText(taskData.text, languageId) : null}
					image={taskData.image}
					deviceInfo={deviceInfo}
					fileName={languageId + '-' + taskData.taskId}
				/>
			</div>

			<div className='OrderPush-items'>
				<OrderDndItems 
					handleSwitchItems={handleSwitchItems} 
					isCompleted={isCompleted}
					itemList={orderedItems}
					languageId={languageId}
					previewList={previewItems}
					currentPreview={currentPreview}
					taskData={taskData}
					handleSetPreview={handleSetPreview}
					handleUnsetPreview={handleUnsetPreview}
				/>
			</div>

			{/* Done button */}
			{!isCompleted && <div className="OrderPush-doneBtn">
				<Button
					classes={['blue']}
					text={getText(playerUiTexts.ok, languageId)}
					onClick={confirmOrder}
				/>
			</div>}

			{/* Popup */}
			{popupData && <Popup 
				type="order-result"
				title={popupData.title}
				text={popupData.text}
				togglePopup={handleClosePopup}
			/>}
		</div>
	);
};

OrderPush.propTypes = {
	playerTaskData: PropTypes.object,
	taskData: PropTypes.object.isRequired,
	languageId: PropTypes.string.isRequired,
	deviceInfo: PropTypes.object.isRequired,
	handleCompleteTask: PropTypes.func.isRequired,
	updateLoggedTime: PropTypes.func.isRequired
};

export default OrderPush;
